import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, useParams, useSearchParams } from 'react-router-dom';

import { ReactComponent as ClockIcon } from '../../../assets/icons/clock-icon.svg';
import { ReactComponent as NavigateArrow } from '../../../assets/icons/device-management/navigate-back.svg';
import { ReactComponent as EditIcon } from '../../../assets/icons/edit.svg';
import { ReactComponent as NavigationIcon } from '../../../assets/icons/location.svg';
import { ReactComponent as PhoneIcon } from '../../../assets/icons/phone-icon.svg';
import { ReactComponent as PlusIcon } from '../../../assets/icons/plus.svg';
import { ReactComponent as SettingIcon } from '../../../assets/icons/setting-icon.svg';
import { brandIcons } from '../../../config/branding';
import { AccountsInterFace } from '../../../interface/account-management.interface';
import {
  AccountStationInterFace,
  StationOperatingHours,
} from '../../../interface/account-stations-interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { clearByMonthData } from '../../../redux/slices/ByMonthRevenueSlice';
import {
  clearByTransactionData,
  fetchByTransactionAccountFilters,
} from '../../../redux/slices/ByTransactionSlice';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import { setPageTitle } from '../../../redux/slices/PageTitleSlice';
import { clearPayoutData, fetchAccountRevenueTotal } from '../../../redux/slices/PayoutSlice';
import { setSelectedStation } from '../../../redux/slices/StationManagementSidePanelSlice';
import { RootState } from '../../../redux/store';
import BatteryLoader from '../../../shared-components/battery-loader/BatteryLoader';
import MyButton from '../../../shared-components/button/Button';
import PageTitle from '../../../shared-components/page-title/PageTitle';
import SelectField from '../../../shared-components/select-field/SelectField';
import FacilityDetailsSidePanel from '../../../shared-components/table-side-panel/FacilityDetailsSidePanel';
import SidePaneModel from '../../../shared-components/table-side-panel/SidePaneModel';
import StationSidePanel from '../../../shared-components/table-side-panel/StationSidePanel';
import TabsContainer from '../../../shared-components/tabs-container/TabsContainer';
import ConvertUTCtoDateTime from '../../../shared-functions/ConvertUTCtoDateTime';
import { convertTo12HourFormat } from '../../../shared-functions/DataTime';
import { permissionCheck } from '../../../shared-functions/JWTDocodeFunction';
import ToFixedValue from '../../../shared-functions/ToFixedValue';
import account from '../../../utils/api/account';
import stationApi from '../../../utils/api/partner/station';
import DashboardWidgets from '../../station-pages/advanced-station-management/components/DashboardWidgets';
import StationManagement from '../../station-pages/station-management';
import AddFacilityAccount from '../components/add-facility-account';

import ByTransactionAccount from './ByTransactionAccount';
import DeviceManagementAccount from './DeviceManagementAccount';
import Devices from './Devices';
import PartnerSearch from './PartnerSearch';
import Payout from './Payout';
import PowerConsumedChart from './PowerConsumedChart';
import RevenueByMonthAccount from './RevenueByMonthAccount';
import RevenueVsPayableChart from './RevenueVsPayableChart';
import SettingAccount from './SettingAccount';
import Stations from './Stations';

interface AccountDetailsDataProperties {
  id: string;
  _id: string;
  accountCustomId: string;
  accountName: string;
  email: string;
  noOfStations: string;
  noOfDevices: string;
  onlineChargers: string | number;
  role: string;
  kwhConsumed: string;
  createdAt: string;
  updatedAt: string;
  stationCount?: string | number;
  totalDeviceCount?: string | number;
  activeDeviceCount?: string | number;
  totalStations?: number | string;
  totalChargers?: number | string;
  powerConsumed?: string;
  // Present on the account-details response but absent from the refresh-safe
  // charging-station list fetch; used only to backfill emergencyContact/countryCode/
  // navigationDirections on top of that list.
  stations?: AccountStationInterFace[];
}

interface AvailableTabProperties {
  label: string;
  key: string;
  isActive: boolean;
  allowed?: boolean;
}

interface ModelDetailsInterface {
  status: boolean;
  content: React.JSX.Element;
  onClose: () => void;
  title: string;
  url?: string | null;
  newTabUrl?: string | null;
  hideHeader?: boolean;
}

/**
 * AccountsManagementPayout component for account management payout page.
 */
function AccountsManagementPayout() {
  const { accounts } = useAppSelector((state: RootState) => state.accounts);
  const { t, i18n } = useTranslation('account');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParameters, setSearchParameters] = useSearchParams();
  /**
   * Back Click
   */
  const handleBackClick = () => {
    const source = location.state?.source;
    if (source) {
      try {
        const url = new URL(source, window.location.origin);
        url.searchParams.delete('accountCustomId');
        url.searchParams.delete('stationId');
        url.searchParams.delete('deviceId');
        navigate(url.pathname + url.search);
      } catch {
        navigate(-1);
      }
    } else {
      navigate(-1);
    }
  };
  const { revenueTotal } = useAppSelector((state) => state.payout);
  const { id } = useParams<{ id: string }>();
  const accountCustomId = id;
  const [activeTab, setActiveTab] = useState<'stations' | 'devices' | 'online' | 'offline' | null>(
    null,
  );
  const [selectedWidgetStationId, setSelectedWidgetStationId] = useState<string | null>(null);
  const [isAccountDetailsLoading, setIsAccountDetailsLoading] = useState(false);
  const [accountStation, setAccountStation] = useState<AccountStationInterFace[]>([]);
  const [accountStationLoading, setAccountStationLoading] = useState(false);

  const [showSettings, setShowSettings] = useState(false);
  const [modelDetails, setModelDetails] = useState<ModelDetailsInterface>({
    status: false,
    content: <div />,
    title: '',
    url: '',
    newTabUrl: '',
    hideHeader: false,
    /**
     * A function to close side panel
     */
    onClose: () => {},
  });
  const [accountDetails, setAccountDetails] = useState<AccountDetailsDataProperties>({
    id: '',
    _id: '',
    accountCustomId: '',
    accountName: '',
    email: '',
    noOfStations: '',
    noOfDevices: '',
    onlineChargers: '',
    role: '',
    kwhConsumed: '',
    createdAt: '',
    updatedAt: '',
  });
  // Backfill map: emergencyContact/countryCode/navigationDirections only come through
  // the account-details response, not the refresh-safe charging-station list fetch.
  const accountDetailsStationsById = useMemo(() => {
    const map: Record<string, AccountStationInterFace> = {};
    (accountDetails.stations || []).forEach((station) => {
      if (station._id) map[station._id] = station;
    });
    return map;
  }, [accountDetails.stations]);

  const stationOptions = accountStation.map((station) => ({
    label: station.name || station.chargingStationName || '',
    value: station._id || '',
  }));

  const [leftActiveTab, setLeftActiveTab] = useState('partnerInfo');
  const [rightActiveTab, setRightActiveTab] = useState('stationsInfo');
  const [bottomActiveTab, setBottomActiveTab] = useState('stations');
  const leftContainerTabs: AvailableTabProperties[] = [
    {
      label: t('partnerInfo', 'Partner Info'),
      key: 'partnerInfo',
      isActive: leftActiveTab === 'partnerInfo',
    },
    {
      label: t('transaction', 'Transaction'),
      key: 'transaction',
      isActive: leftActiveTab === 'transaction',
    },
    {
      label: t('power', 'Power'),
      key: 'power',
      isActive: leftActiveTab === 'power',
    },
  ];
  const rightContainerTabs: AvailableTabProperties[] = [
    {
      label: t('stationsInfo', 'Stations Info'),
      key: 'stationsInfo',
      isActive: rightActiveTab === 'stationsInfo',
    },
    {
      label: t('chargers', 'Chargers'),
      key: 'chargers',
      isActive: rightActiveTab === 'chargers',
    },
  ];
  const bottomContainerTabs: AvailableTabProperties[] = [
    {
      label: t('stations', 'Stations'),
      key: 'stations',
      isActive: bottomActiveTab === 'stations',
    },
    {
      label: t('devices', 'Devices'),
      key: 'devices',
      isActive: bottomActiveTab === 'devices',
    },
    {
      label: t('revenueByTransaction', 'Revenue By Transaction'),
      key: 'byTransaction',
      isActive: bottomActiveTab === 'byTransaction',
    },
    {
      label: t('revenueByMonth', 'Revenue By Month'),
      key: 'byMonth',
      isActive: bottomActiveTab === 'byMonth',
    },
    {
      label: t('payout', 'Payout'),
      key: 'payout',
      isActive: bottomActiveTab === 'payout',
    },
  ];

  const accountInfo = [
    {
      label: t('accountId', 'Account ID'),
      value: accountDetails?.accountCustomId,
    },
    {
      label: t('name', 'Name'),
      value: accountDetails?.accountName,
    },
    {
      label: t('email', 'Email'),
      value: accountDetails?.email,
    },
    {
      label: t('role', 'Role'),
      value: accountDetails?.role,
    },
    {
      label: t('noOfStations', 'No.Of Stations'),
      value: accountDetails?.stationCount,
    },
    {
      label: t('kwhConsumed', 'kWh Consumed'),
      value: ToFixedValue(accountDetails?.powerConsumed, 3),
    },
    {
      label: t('created', 'Created'),
      value: ConvertUTCtoDateTime(accountDetails?.createdAt),
    },
    {
      label: t('lastUpdated', 'Last Updated'),
      value: ConvertUTCtoDateTime(accountDetails?.updatedAt),
    },
  ];

  const stationAmenityIcons = [
    { name: t('gasStation'), key: 'Gas Station', svgIconActive: <brandIcons.GasStationActive /> },
    { name: t('wifi'), key: 'Free Wifi', svgIconActive: <brandIcons.WifiActive /> },
    { name: t('restroom'), key: 'Restroom', svgIconActive: <brandIcons.RestroomActive /> },
    { name: t('lounge'), key: 'Lounge', svgIconActive: <brandIcons.LoungeActive /> },
    { name: t('cafe'), key: 'Cafe', svgIconActive: <brandIcons.CaffeeActive /> },
    { name: t('shopping'), key: 'Shopping', svgIconActive: <brandIcons.ShoppingActive /> },
    { name: t('restaurant'), key: 'Restaurant', svgIconActive: <brandIcons.RestaurantActive /> },
    { name: t('relaxingArea'), key: 'Relaxing Area', svgIconActive: <brandIcons.RelaxingActive /> },
    { name: t('carWash'), key: 'Car Wash', svgIconActive: <brandIcons.CarwashActive /> },
  ];

  /**
   * Get a compact label for a station's operating hours on the current day
   * @param {StationOperatingHours} operatingHours weekly operating hours data
   * @returns {string | null} label such as "Open 24 Hours", "09:00 AM-06:00 PM", or "Closed Today"
   */
  const getTodayHoursLabel = (operatingHours?: StationOperatingHours): string | null => {
    if (!operatingHours) return null;
    const dayNames = [
      'sunday',
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
    ] as const;
    const today = operatingHours[dayNames[new Date().getDay()]];
    if (!today || today.status !== 'open') {
      return t('closedToday', 'Closed Today');
    }
    if (today.open === '00:00' && today.close === '23:59') {
      return t('open24Hours', 'Open 24 Hours');
    }
    if (today.open && today.close) {
      return `${convertTo12HourFormat(today.open)}-${convertTo12HourFormat(today.close)}`;
    }
    return null;
  };

  /**
   * Fetch Account Details  Data
   */
  const fetchAccountDetails = async () => {
    if (!accountCustomId) {
      return;
    }
    setAccountDetails({
      id: '',
      _id: '',
      accountCustomId: '',
      accountName: '',
      email: '',
      noOfStations: '',
      noOfDevices: '',
      onlineChargers: '',
      role: '',
      kwhConsumed: '',
      createdAt: '',
      updatedAt: '',
    });
    setIsAccountDetailsLoading(true);
    try {
      const response = await account.fetchAccountDetails(accountCustomId);
      setAccountDetails((previous: AccountDetailsDataProperties) => {
        // Create a new object merging previous data (from redux) and new data (from api)
        // Prioritize API data, but keep stats from Redux if API doesn't provide them
        const newData = { ...previous, ...response.data };

        // Ensure stats are preserved if they exist in previous but not/empty in response
        if (!newData.stationCount && previous.stationCount)
          newData.stationCount = previous.stationCount;
        if (!newData.totalDeviceCount && previous.totalDeviceCount)
          newData.totalDeviceCount = previous.totalDeviceCount;
        if (!newData.activeDeviceCount && previous.activeDeviceCount)
          newData.activeDeviceCount = previous.activeDeviceCount;
        if (!newData.powerConsumed && previous.powerConsumed)
          newData.powerConsumed = previous.powerConsumed;
        return newData;
      });
    } catch (error: any) {
      setAccountDetails({
        id: '',
        _id: '',
        accountCustomId: '',
        accountName: '',
        email: '',
        noOfStations: '',
        noOfDevices: '',
        onlineChargers: '',
        role: '',
        kwhConsumed: '',
        createdAt: '',
        updatedAt: '',
      });
      dispatch(
        setNotificationMessage({
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || t('somethingWentWrong'),
          },
        }),
      );
    } finally {
      setIsAccountDetailsLoading(false);
    }
  };

  /**
   * Fetch the stations belonging to this account, including operating hours,
   * amenities and contact details, straight from the charging-station list API
   * so the data is always fresh (independent of any cached account list).
   */
  const fetchAccountStationList = async () => {
    if (!accountCustomId) {
      return;
    }
    setAccountStationLoading(true);
    try {
      const response = await stationApi.fetchStations({
        accountCustomIds: [accountCustomId],
        limit: 100,
        page: 1,
      });
      setAccountStation(response.data.results || []);
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || t('somethingWentWrong'),
          },
        }),
      );
    } finally {
      setAccountStationLoading(false);
    }
  };

  /**
   * Function to switch the tab
   * @param {string} key - The key
   */
  const handleLeftSwitchTab = (key: string) => {
    setLeftActiveTab(key);
  };

  /**
   * Function to switch the tab
   * @param {string} key - The key
   */
  const handleRightSwitchTab = (key: string) => {
    setRightActiveTab(key);
  };

  /**
   * Function to switch the tab
   * @param {string} key - The key
   */
  const handleBottomSwitchTab = (key: string) => {
    setBottomActiveTab(key);
  };

  /**
   * function for closing side panel
   */
  const handleClosePanel = () => {
    setModelDetails((previous) => ({
      ...previous,
      status: false,
    }));
  };

  /**
   * Handle open station side panel
   * @param {string} stationId - The station ID to display in the side panel
   */
  const handleOpenStationSidePanel = (stationId: string) => {
    if (
      searchParameters.has('stationId') ||
      searchParameters.has('deviceId') ||
      searchParameters.has('accountCustomId')
    ) {
      const parameters = new URLSearchParams(searchParameters);
      parameters.delete('stationId');
      parameters.delete('deviceId');
      parameters.delete('accountCustomId');

      setSearchParameters(parameters, { replace: true });
    }
    if (stationId) {
      setSelectedWidgetStationId(stationId);
      const targetStation = accountStation.find((station) => station._id === stationId);
      if (targetStation) {
        dispatch(setSelectedStation({ id: targetStation._id, name: targetStation.name }));
      }
    }
    setModelDetails((previous) => ({
      ...previous,
      status: true,
      title: 'Station Details',
      url: '',
      newTabUrl: '',
      hideHeader: true,
      content: (
        <StationSidePanel
          fetchIdFrom="props"
          id={stationId || selectedWidgetStationId || ''}
          onClose={handleClosePanel}
        />
      ),
    }));
  };

  /**
   * Handle open add partner
   */
  const handleOpenAddPartnerSidePanel = () => {
    if (
      searchParameters.has('stationId') ||
      searchParameters.has('deviceId') ||
      searchParameters.has('accountCustomId')
    ) {
      const parameters = new URLSearchParams(searchParameters);
      parameters.delete('stationId');
      parameters.delete('deviceId');
      parameters.delete('accountCustomId');

      setSearchParameters(parameters, { replace: true });
    }
    setModelDetails((previous) => ({
      ...previous,
      status: true,
      title: 'Add Partner',
      url: '',
      newTabUrl: '',
      hideHeader: false,
      content: (
        <AddFacilityAccount closeModal={handleClosePanel} sidebarClosed={handleClosePanel} />
      ),
    }));
  };

  /**
   * Handle open add partner
   */
  const handleOpenEditPartnerSidePanel = () => {
    if (
      searchParameters.has('stationId') ||
      searchParameters.has('deviceId') ||
      searchParameters.has('accountCustomId')
    ) {
      const parameters = new URLSearchParams(searchParameters);
      parameters.delete('stationId');
      parameters.delete('deviceId');
      parameters.delete('accountCustomId');

      setSearchParameters(parameters, { replace: true });
    }
    setModelDetails((previous) => ({
      ...previous,
      status: true,
      title: 'Edit Partner',
      url: '',
      newTabUrl: '',
      hideHeader: false,
      content: (
        <FacilityDetailsSidePanel
          fetchIdFrom="props"
          id={accountDetails?.accountCustomId || ''}
          isEditMode
          onBack={handleClosePanel}
          onSuccess={() => {
            handleClosePanel();
            fetchAccountDetails();
          }}
        />
      ),
    }));
  };

  useEffect(() => {
    setSelectedWidgetStationId(null);
    if (accountCustomId) {
      fetchAccountDetails();
      fetchAccountStationList();
      dispatch(fetchAccountRevenueTotal({ accountCustomId }));
    }
  }, [accountCustomId]);

  useEffect(() => {
    if (accountStation.length > 0) {
      const hasSelectedStation = accountStation.some(
        (station) => station._id === selectedWidgetStationId,
      );
      if (!hasSelectedStation) {
        const firstStation = accountStation[0];
        setSelectedWidgetStationId(firstStation._id || null);
        dispatch(setSelectedStation({ id: firstStation._id, name: firstStation.name }));
      }
    }
  }, [accountStation, selectedWidgetStationId, dispatch]);

  // Fetch filters for the account on mount and clear them on unmount.
  useEffect(() => {
    if (accountCustomId) {
      dispatch(fetchByTransactionAccountFilters({ accountCustomId }));
    }
    // Cleanup on unmount: clears ByTransaction filters
    return () => {
      dispatch(clearByTransactionData());
      dispatch(clearByMonthData());
      dispatch(clearPayoutData());
    };
  }, [accountCustomId, dispatch]);

  useEffect(() => {
    dispatch(
      setPageTitle({
        title: t('partners'),
      }),
    );
  }, [t, dispatch]);

  useEffect(() => {
    if (accountCustomId && accounts.length > 0) {
      const found = accounts.find(
        // eslint-disable-next-line @typescript-eslint/no-shadow
        (account: AccountsInterFace) =>
          account.id === accountCustomId ||
          account._id === accountCustomId ||
          account.accountCustomId === accountCustomId,
      );
      if (found) {
        setAccountDetails((previous: AccountDetailsDataProperties) => ({
          // Merge to preserve details already fetched
          ...previous,
          ...found,
        }));
      }
    }
  }, [accountCustomId, accounts]);

  return (
    <div className="accounts-management-payout">
      <SidePaneModel
        open={modelDetails.status}
        content={modelDetails.content}
        closePanel={handleClosePanel}
        title={modelDetails.title}
        url={modelDetails.url || ''}
        urlNewTab={modelDetails.newTabUrl || ''}
        hideHeader={modelDetails.hideHeader}
        preventQueryClear
      />
      <div className="title-content" dir={i18n.dir()}>
        <div className="back-arrow">
          <button type="button" className="navigate-arrow" onClick={handleBackClick}>
            <span>
              <NavigateArrow />
            </span>
            <div className="navigate-background" />
          </button>
          <PageTitle title={t('facilityDetails')} />
        </div>{' '}
        <div className="title-content-right">
          <PartnerSearch />
          <div className="title-content-right-buttons">
            <MyButton
              label={t('addAccount', 'Add Partner')}
              buttonType="outline"
              svgIcon={<PlusIcon />}
              onClickFunc={handleOpenAddPartnerSidePanel}
              disableButton={!permissionCheck('cpoPartnerAdd')}
            />
            <MyButton
              label={t('editAccount', 'Edit Partner')}
              buttonType="outline"
              svgIcon={<EditIcon />}
              onClickFunc={handleOpenEditPartnerSidePanel}
              disableButton={!permissionCheck('cpoPartnerUpdate')}
            />
          </div>
        </div>
      </div>

      <div className="payout-dashboard-top-grid">
        {/* Left Column: Data & Info */}
        <div className="payout-left-column">
          <div className="tab-switch-container">
            <TabsContainer
              containerTabs={leftContainerTabs}
              handleSwitchTab={handleLeftSwitchTab}
            />
            <SettingIcon
              className="icon"
              onClick={() => setShowSettings((previous) => !previous)}
            />
            {showSettings && (
              <div className="settings-dropdown">
                <SettingAccount setShowSettings={setShowSettings} />
              </div>
            )}
          </div>

          <div className="left-tab-content">
            {leftActiveTab === 'partnerInfo' && isAccountDetailsLoading && (
              <div className="loader-wrap">
                <BatteryLoader />
              </div>
            )}
            {leftActiveTab === 'partnerInfo' && !isAccountDetailsLoading && (
              <div className="partner-info-tab">
                <div className="account-card">
                  {/* Left Section */}
                  <div className="account-info">
                    {accountInfo.map(({ label, value }) => (
                      <div className="info-item" key={label}>
                        <p className="value">{value || '--'}</p>
                        <p className="label">{label}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="account-summaries-row">
                  <div className="account-summary">
                    <div className="summary-item">
                      <span className="label">{t('totalRevenue')}</span>
                      <div className="value-box">
                        <brandIcons.currencyIcon className="icon" />
                        <span className="value">
                          {ToFixedValue(revenueTotal?.totalRevenue || 0)}
                        </span>
                      </div>
                    </div>

                    <div className="summary-item">
                      <span className="label">{t('totalPaidRevenue')}</span>
                      <div className="value-box">
                        <brandIcons.currencyIcon className="icon" />
                        <span className="value">
                          {ToFixedValue(revenueTotal?.totalPaidRevenue || 0)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="account-summary">
                    <div className="summary-item">
                      <span className="label">{t('payableAmount')}</span>
                      <div className="value-box">
                        <brandIcons.currencyIcon className="icon" />
                        <span className="value">
                          {ToFixedValue(revenueTotal?.pendingBalanceFromLastPayout || 0)}
                        </span>
                      </div>
                    </div>
                    <div className="summary-item">
                      <span className="label">{t('netPayableAmount')}</span>
                      <div className="value-box">
                        <brandIcons.currencyIcon className="icon" />
                        <span className="value">
                          {ToFixedValue(revenueTotal?.pendingBalance || 0)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {leftActiveTab === 'transaction' && (
              <div className="chart-wrapper">
                <RevenueVsPayableChart />
              </div>
            )}

            {leftActiveTab === 'power' && (
              <div className="chart-wrapper">
                <PowerConsumedChart />
              </div>
            )}
          </div>
        </div>

        {/* Right Column: Charts */}
        <div className="payout-right-column">
          <div>
            <TabsContainer
              containerTabs={rightContainerTabs}
              handleSwitchTab={handleRightSwitchTab}
            />
          </div>

          <div className="right-tab-content">
            {rightActiveTab === 'stationsInfo' && (
              <div className="stations-card-list">
                {accountStationLoading && (
                  <div className="loader-wrap">
                    <BatteryLoader />
                  </div>
                )}
                {!accountStationLoading && accountStation.length === 0 && (
                  <div className="no-stations">{t('noDataFound', 'No Data Found')}</div>
                )}
                {!accountStationLoading &&
                  accountStation.length > 0 &&
                  accountStation.map((station) => {
                    const onlineCount =
                      (station as any).online ??
                      (station as any).onlineChargersCount ??
                      (station as any).onlineChargers ??
                      0;
                    const offlineCount =
                      (station as any).offline ??
                      (station as any).offlineChargersCount ??
                      (station as any).offlineChargers ??
                      0;
                    const isAvailable = (station as any).isAvailable !== false;
                    const enrichedDetails = station._id
                      ? accountDetailsStationsById[station._id]
                      : undefined;
                    const emergencyContact =
                      station.emergencyContact || enrichedDetails?.emergencyContact;
                    const countryCode = station.countryCode || enrichedDetails?.countryCode;
                    const hoursLabel = getTodayHoursLabel(station.operatingHours);
                    const primaryDirection = (
                      station.navigationDirections || enrichedDetails?.navigationDirections
                    )?.find((item) => item.direction);
                    const amenities = station.amenities ?? [];
                    const visibleAmenities = amenities.slice(0, 4);
                    const extraAmenitiesCount = amenities.length - visibleAmenities.length;

                    return (
                      <div
                        key={station._id}
                        className="station-details-card"
                        role="presentation"
                        onClick={() => handleOpenStationSidePanel(station._id || '')}>
                        <div className="station-card-top">
                          <span className="station-name">
                            {station.name || station.chargingStationName}
                          </span>
                          <span className="partner-name">{accountDetails?.accountName ?? ''}</span>
                        </div>
                        <div className="station-info-list">
                          <div className="info-row">
                            <span className="info-label">
                              <NavigationIcon />
                              {t('location', 'Location')}
                            </span>
                            <span className="info-value" title={station.address || 'N/A'}>
                              {station.address || 'N/A'}
                            </span>
                          </div>
                          {emergencyContact && (
                            <div className="info-row">
                              <span className="info-label">
                                <PhoneIcon />
                                {t('contact', 'Contact')}
                              </span>
                              <span className="info-value">
                                {countryCode ? `+${countryCode} ` : ''}
                                {emergencyContact}
                              </span>
                            </div>
                          )}
                          {hoursLabel && (
                            <div className="info-row">
                              <span className="info-label">
                                <ClockIcon />
                                {t('hours', 'Hours')}
                              </span>
                              <span className="info-value">{hoursLabel}</span>
                            </div>
                          )}
                          {primaryDirection && (
                            <div className="info-row">
                              <span className="info-label">
                                <NavigationIcon />
                                {t('direction', 'Directions')}
                              </span>
                              <span className="info-value" title={primaryDirection.direction}>
                                {primaryDirection.direction}
                              </span>
                            </div>
                          )}
                        </div>
                        {amenities.length > 0 && (
                          <div className="station-card-amenities">
                            {visibleAmenities.map((amenity) => {
                              const amenityDetail = stationAmenityIcons.find(
                                (each) => each.key === amenity,
                              );
                              if (!amenityDetail) return null;
                              return (
                                <span
                                  className="amenity-icon"
                                  key={amenity}
                                  title={amenityDetail.name}>
                                  {amenityDetail.svgIconActive}
                                </span>
                              );
                            })}
                            {extraAmenitiesCount > 0 && (
                              <span className="amenity-more">+{extraAmenitiesCount}</span>
                            )}
                          </div>
                        )}
                        <div className="station-card-bottom">
                          <div className="counts">
                            <span className="count-item online">
                              {t('onlineCharger')}: <strong>{onlineCount}</strong>
                            </span>
                            <span className="count-item offline">
                              {t('offlineCharger')}: <strong>{offlineCount}</strong>
                            </span>
                          </div>
                          <span
                            className={`status-badge ${isAvailable ? 'available' : 'unavailable'}`}>
                            {isAvailable
                              ? t('available', 'Available')
                              : t('unavailable', 'Unavailable')}
                          </span>
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
            {rightActiveTab === 'chargers' && (
              <div className="charger-info-tab-content">
                {accountStation.length > 0 && (
                  <div className="station-selector-wrapper">
                    <label className="selector-label" htmlFor="station-widget-select">
                      {t('selectStation', 'Select Station')}:{' '}
                    </label>
                    <SelectField
                      name="station"
                      options={stationOptions}
                      selectedValue={selectedWidgetStationId || ''}
                      onChangeValue={(name: string, value: string) => {
                        setSelectedWidgetStationId(value);

                        const found = accountStation.find((station) => station._id === value);

                        if (found) {
                          dispatch(
                            setSelectedStation({
                              id: found._id,
                              name: found.name,
                            }),
                          );
                        }
                      }}
                    />
                  </div>
                )}
                <div className="widgets-container">
                  {accountStationLoading && (
                    <div className="loader-wrap">
                      <BatteryLoader />
                    </div>
                  )}
                  {!accountStationLoading && selectedWidgetStationId && <DashboardWidgets />}
                  {!accountStationLoading && !selectedWidgetStationId && (
                    <div className="no-stations">{t('noDataFound', 'No Data Found')}</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="bottom-line" />

      <TabsContainer containerTabs={bottomContainerTabs} handleSwitchTab={handleBottomSwitchTab} />
      <div className="accounts-management-table">
        {(!bottomActiveTab || bottomActiveTab === 'payout') && <Payout />}
        {bottomActiveTab === 'byTransaction' && <ByTransactionAccount />}
        {bottomActiveTab === 'byMonth' && <RevenueByMonthAccount />}
        {bottomActiveTab === 'stations' && (
          <StationManagement fetchByPartner={id} preventNavigation />
        )}
        {bottomActiveTab === 'devices' && <DeviceManagementAccount accountCustomId={id} />}
      </div>

      {/* Stations / Devices Popup */}
      {activeTab && (
        <div className="stations-devices-container">
          {activeTab === 'stations' && (
            <div>
              <Stations onClose={() => setActiveTab(null)} />
            </div>
          )}
          {activeTab === 'online' && (
            <div>
              <Devices onClose={() => setActiveTab(null)} isDeviceOffline={false} />
            </div>
          )}

          {activeTab === 'offline' && (
            <div>
              <Devices onClose={() => setActiveTab(null)} isDeviceOffline />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AccountsManagementPayout;
